import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Logo, colors } from "brand";
import { fetchConfig, requestScoreSheet } from "../api";

// The big centered brand logo. Deliberately NOT a link (#631) — a clickable
// logo is an invisible affordance; the score-site link is an explicit button
// below it instead.
function BrandHero() {
  return (
    <Box sx={{ color: colors.foxOrange, width: { xs: 240, sm: 320 } }}>
      <Logo style={{ width: "100%", height: "auto", display: "block" }} />
    </Box>
  );
}

// Card #1957 (bug 181) — show labels carry the DATE so a rider can tell two
// shows apart at a glance, including two shows with near-identical names on
// the same or adjacent days (the real Bello Acres Summer Dressage 1 / 2
// case). Dates are plain YYYY-MM-DD strings; a single-day show shows one
// date, a multi-day show shows its range.
function dateLabel(show) {
  if (!show?.startDate) return "";
  if (!show.endDate || show.endDate === show.startDate) return show.startDate;
  return `${show.startDate} – ${show.endDate}`;
}

function showOptionLabel(show) {
  if (!show) return "";
  const date = dateLabel(show);
  return date ? `${show.name} (${date})` : show.name;
}

// Card #2380 — demo-only prefill, no env var, no build flag. The mechanism
// IS the safety: a production visitor's URL carries no `email`/`cno` query
// params, so this returns "" for both, identical to today's initial state
// (AC5). Only someone handed the actual demo deep link (built from card
// #2376's seeded rider/CNO pair) ever sees anything filled in. Exported as
// a pure function, house convention (see ScoreboardPage's
// buildScoreboardQrUrl, ClassResultsPage's groupResultsByDivision) of
// pinning the exact function the component calls rather than re-deriving
// URLSearchParams handling in a component-render test.
export function prefillFromSearchParams(searchParams) {
  return {
    email: searchParams.get("email") || "",
    cno: searchParams.get("cno") || "",
  };
}

export default function LandingPage() {
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(false);
  const [selectedShow, setSelectedShow] = useState(null);

  const initialPrefill = prefillFromSearchParams(searchParams);
  const [email, setEmail] = useState(initialPrefill.email);
  const [cno, setCno] = useState(initialPrefill.cno);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { severity, message }

  useEffect(() => {
    let active = true;
    fetchConfig()
      .then((data) => {
        if (!active) return;
        setConfig(data);
        // Card #1957 — default to whichever show(s) report-api says are
        // current (or most recent, when none is running); the dropdown still
        // lets a visitor pick any other show. A visitor picking a different
        // show before this resolves just gets overwritten here — fine, this
        // only runs once, right after the one config fetch.
        const shows = data?.shows || [];
        const defaultIds = new Set(data?.defaultShowIds || []);
        const initial = shows.find((show) => defaultIds.has(show.id)) || shows[0] || null;
        setSelectedShow(initial);
      })
      .catch(() => {
        if (active) setConfigError(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedShow) return;
    setSubmitting(true);
    setResult(null);
    try {
      const { status, body } = await requestScoreSheet({
        email,
        cno,
        showId: selectedShow.id,
      });
      if (status === 202 && body.found) {
        setResult({ severity: "success", message: body.message });
      } else if (status === 200) {
        setResult({ severity: "warning", message: body.message });
      } else if (status === 400) {
        setResult({
          severity: "error",
          message:
            body.error || "Please check your email address and competitor number.",
        });
      } else {
        setResult({
          severity: "error",
          message:
            "This service is temporarily unavailable. Please try again in a little while.",
        });
      }
    } catch {
      setResult({
        severity: "error",
        message:
          "This service is temporarily unavailable. Please try again in a little while.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const shows = config?.shows || [];
  // Card #1957 AC9 (bug 181) — this used to be RIDER_SHEETS_SCORE_SITE_URL,
  // a per-show env var. report-api now always sends the same generic
  // https://foxvillage.com/Results, so "not configured" no longer means "no
  // link" — it means "no show data at all" (an empty score-show table),
  // which is a genuinely different, rarer state than before.
  const notAvailable = config?.configured === false;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Stack spacing={4} alignItems="center" sx={{ width: "100%", maxWidth: 520 }}>
        <BrandHero />

        {/* #631/#1957 — the score-site link is an explicit button, not the
            logo, and is now a fixed generic URL (bug 181) rather than a
            per-show one, so it always renders live once config has loaded. */}
        {config?.scoreSiteUrl && (
          <Button
            component="a"
            href={config.scoreSiteUrl}
            target="_blank"
            rel="noopener"
            variant="contained"
            size="large"
            endIcon={<OpenInNewIcon />}
            sx={{
              bgcolor: colors.foxOrange,
              color: colors.darkRed,
              fontWeight: 700,
              textTransform: "none",
              fontSize: { xs: "1rem", sm: "1.15rem" },
              px: 3,
              py: 1.5,
              textAlign: "center",
              "&:hover": {
                bgcolor: colors.foxOrange,
                filter: "brightness(0.92)",
              },
            }}
          >
            Get all the scores here!
          </Button>
        )}

        {configError && (
          <Alert severity="error" sx={{ width: "100%" }}>
            We couldn&apos;t load this page right now. Please try again later.
          </Alert>
        )}

        {!configError && notAvailable && (
          <Alert severity="info" sx={{ width: "100%" }}>
            Complete score sheets aren&apos;t available right now. Please
            check back later.
          </Alert>
        )}

        {!configError && config && !notAvailable && (
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Get your complete score sheet!
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 2 }}
              >
                Pick your show, then enter the email address and competitor
                number you used there, and we&apos;ll send you a link to all
                your score sheets.
              </Typography>
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  {/* Card #1957 AC3/AC4 — a dropdown with typeahead, sitting
                      right here with the CNO/email fields (never an on-page
                      list of every show, which would push these fields below
                      the fold). Sorted reverse-chronological by report-api. */}
                  <Autocomplete
                    options={shows}
                    value={selectedShow}
                    onChange={(_event, value) => setSelectedShow(value)}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    getOptionLabel={showOptionLabel}
                    disabled={submitting}
                    disableClearable
                    renderInput={(params) => (
                      <TextField {...params} label="Show" required />
                    )}
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    fullWidth
                    disabled={submitting}
                  />
                  <TextField
                    label="Competitor # (CNO)"
                    value={cno}
                    onChange={(e) => setCno(e.target.value)}
                    required
                    fullWidth
                    disabled={submitting}
                  />
                  {result && (
                    <Alert severity={result.severity}>{result.message}</Alert>
                  )}
                  {/* Brand orange, not MUI `primary` — the brand primary is a
                      dark grey that nearly disappears on a dark background, and
                      this is the page's main call to action. */}
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={submitting || !selectedShow}
                    startIcon={
                      submitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                    sx={{
                      bgcolor: colors.foxOrange,
                      color: colors.darkRed,
                      fontWeight: 700,
                      "&:hover": {
                        bgcolor: colors.foxOrange,
                        filter: "brightness(0.92)",
                      },
                    }}
                  >
                    {submitting ? "Sending…" : "Email me my score sheets"}
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        )}

        {!configError && !config && !notAvailable && (
          <CircularProgress />
        )}
      </Stack>
    </Box>
  );
}
